import { useRef, useState } from "react";
import { useKeyboard } from "../routes/present/useKeyboard";
import { usePresenter } from "./context";
import { Footnotes, Sup } from "./Footnotes";
import type { VideoBlock } from "./model";
import { SlideHeading } from "./SlideHeading";

/** The clip's box on the stage: under the heading, above the footnotes. */
const LEFT = 64;
const WIDTH = 1152;
const BOTTOM = 600;

/**
 * A clip on the projector. It plays when the slide opens (muted by default, so the browser allows it), between
 * the block's start and end, looping if asked. K or the button pauses; Next always moves on. A GIF is an image.
 * Studio previews show the first frame and don't play.
 */
export function VideoSlide({ block }: { block: VideoBlock }) {
  const { preview } = usePresenter();
  const video = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [blocked, setBlocked] = useState(false);
  // A GIF is uploaded and served as an image; its name says so (it comes from the upload).
  const gif = /\.gif$/i.test(block.filename);
  const src = block.assetId ? `/api/assets/${encodeURIComponent(block.assetId)}` : null;
  const start = block.start ?? 0;
  const top = block.lead ? 236 : 184;
  const bottom = BOTTOM - (block.caption || !preview ? 44 : 0);

  const play = () => {
    const v = video.current;
    if (!v) return;
    if (block.end !== undefined && v.currentTime >= block.end) v.currentTime = start;
    v.play().then(
      () => setBlocked(false),
      () => setBlocked(true), // e.g. sound on and the browser wants a click first
    );
  };
  const toggle = () => {
    const v = video.current;
    if (!v) return;
    if (v.paused) play();
    else v.pause();
  };
  const restart = () => {
    const v = video.current;
    if (!v) return;
    v.currentTime = start;
    play();
  };

  useKeyboard(preview ? {} : { k: toggle });

  const onTime = () => {
    const v = video.current;
    if (!v || block.end === undefined || v.currentTime < block.end) return;
    if (block.loop) v.currentTime = start;
    else v.pause();
  };

  const media = !src ? (
    <div className="video-empty">
      <span className="video-empty-name">{block.filename || "No clip yet"}</span>
      Upload the clip in the studio.
    </div>
  ) : gif ? (
    <img className="video-media" src={src} alt={block.title} />
  ) : (
    <video
      ref={video}
      className="video-media"
      src={`${src}#t=${start}`}
      muted={block.muted}
      // Native looping only covers the whole clip; a trimmed loop is handled in onTime.
      loop={block.loop && block.end === undefined && start === 0}
      playsInline
      preload={preview ? "metadata" : "auto"}
      autoPlay={!preview}
      onPlay={() => setPlaying(true)}
      onPause={() => setPlaying(false)}
      onTimeUpdate={onTime}
      onEnded={() => block.loop && restart()}
      onClick={preview ? undefined : toggle}
      onLoadedMetadata={(e) => {
        if (start) e.currentTarget.currentTime = start;
        if (!preview) play();
      }}
      aria-label={block.title}
    />
  );

  return (
    <div className="slide">
      <SlideHeading
        blockId={block.id}
        title={block.title}
        lead={
          block.lead && (
            <>
              {block.lead}
              <Sup refs={block.refs} />
            </>
          )
        }
      />
      <div className="abs video-frame" style={{ left: LEFT, top, width: WIDTH, height: bottom - top }}>
        {media}
        {blocked && (
          <button type="button" className="video-blocked" onClick={play}>
            Click to play
          </button>
        )}
      </div>
      <div className="abs video-foot" style={{ left: LEFT, top: bottom + 12, width: WIDTH }}>
        <span className="video-caption">{block.caption}</span>
        {!preview && src && !gif && (
          <span className="video-controls">
            <button type="button" className="btn" onClick={restart}>
              Restart
            </button>
            <button type="button" className="btn" onClick={toggle} aria-keyshortcuts="K">
              {playing ? "Pause" : "Play"}
            </button>
          </span>
        )}
      </div>
      <Footnotes refs={block.refs} />
    </div>
  );
}
